import * as MP4Box from 'mp4box';
import type { DemuxConfig, TrackInfo } from './types';

/**
 * MP4 Demuxer - Extract encoded chunks from MP4 container
 * Simplified implementation following Stream API pattern
 */
export class MP4Demuxer {
  private mp4boxFile: any;
  tracks = new Map<number, TrackInfo>();
  isReady = false;
  private videoController?: TransformStreamDefaultController<EncodedVideoChunk>;
  private audioController?: TransformStreamDefaultController<EncodedAudioChunk>;
  private demuxHighWaterMark: number;
  private onReadyCallback?: () => void;
  private fileOffset = 0;
  private videoTimestampOffset: number | null = null;
  private audioTimestampOffset: number | null = null;

  constructor(config: DemuxConfig & { onReady?: () => void } = {}) {
    this.mp4boxFile = MP4Box.createFile();
    this.onReadyCallback = config.onReady;

    // Use provided config with local default as fallback
    const DEFAULT_HIGH_WATER_MARK = 10; // Default for video chunks
    this.demuxHighWaterMark = config.highWaterMark ?? DEFAULT_HIGH_WATER_MARK;

    this.setupHandlers();
  }

  updateConfig(config: DemuxConfig): void {
    this.demuxHighWaterMark = config.highWaterMark ?? this.demuxHighWaterMark;
  }

  private setupHandlers(): void {
    this.mp4boxFile.onError = (error: string) => {
      console.error('[MP4Demuxer] MP4Box error:', error);
      const err = new Error(error);
      this.videoController?.error(err);
      this.audioController?.error(err);
    };

    this.mp4boxFile.onReady = (info: any) => {
      this.processTracks(info.tracks);
      this.isReady = true;

      // Call the ready callback before starting
      if (this.onReadyCallback) {
        this.onReadyCallback();
      }

      // Start processing samples after callback
      // Note: start() enables extraction, actual samples will be output
      // when flush() is called (by TransformStream's flush callback)
      this.mp4boxFile.start();
    };

    this.mp4boxFile.onSamples = (trackId: number, _user: any, samples: any[]) => {
      this.processSamples(trackId, samples);
    };
  }

  private processTracks(tracks: any[]): void {
    for (const track of tracks) {
      const trackInfo: TrackInfo = {
        id: track.id,
        type: track.type === 'video' ? 'video' : 'audio',
        codec: track.codec,
        timescale: track.timescale,
      };

      if (track.type === 'video') {
        trackInfo.width = track.video?.width;
        trackInfo.height = track.video?.height;
        trackInfo.description = this.getVideoDescription(track);
      } else if (track.type === 'audio') {
        trackInfo.sampleRate = track.audio?.sample_rate;
        trackInfo.numberOfChannels = track.audio?.channel_count;
        trackInfo.description = this.getAudioDescription(track);
      }

      this.tracks.set(track.id, trackInfo);

      // Configure extraction - use Infinity to extract all samples
      // Note: onSamples will be called multiple times as data is appended,
      // but we don't need to manually request more batches
      this.mp4boxFile.setExtractionOptions(track.id, track, {
        nbSamples: Infinity,
      });
    }
  }

  private processSamples(trackId: number, samples: any[]): void {
    const track = this.tracks.get(trackId);
    if (!track) return;

    const timescale = track.timescale || 90000;
    for (const sample of samples) {
      const rawTimestamp = (sample.cts * 1000000) / timescale;
      const duration = (sample.duration * 1000000) / timescale;

      if (track.type === 'video') {
        if (!this.videoController) {
          console.error('[MP4Demuxer] videoController is null when trying to output chunk!');
          // Should not happen - stream should be created before appendBuffer
          return;
        }

        // Normalize timestamp: first frame starts at 0
        if (this.videoTimestampOffset === null) {
          this.videoTimestampOffset = rawTimestamp;
        }
        const timestamp = rawTimestamp - this.videoTimestampOffset;

        const chunk = new EncodedVideoChunk({
          type: sample.is_sync ? 'key' : 'delta',
          timestamp,
          duration,
          data: sample.data,
        });
        this.videoController.enqueue(chunk);
      } else if (track.type === 'audio' && this.audioController) {
        // Normalize timestamp: first frame starts at 0
        if (this.audioTimestampOffset === null) {
          this.audioTimestampOffset = rawTimestamp;
        }
        const timestamp = rawTimestamp - this.audioTimestampOffset;

        const chunk = new EncodedAudioChunk({
          type: 'key',
          timestamp,
          duration,
          data: sample.data,
        });
        this.audioController.enqueue(chunk);
      }
    }

    const last = samples[samples.length - 1].number;
    // Release memory immediately
    this.mp4boxFile.releaseUsedSamples(trackId, last + 1);
  }

  private getVideoDescription(track: any): ArrayBuffer | undefined {
    try {
      const fullTrack = this.mp4boxFile.getTrackById(track.id);
      for (const entry of fullTrack.mdia.minf.stbl.stsd.entries) {
        const box = entry.avcC ?? entry.hvcC ?? entry.av1C ?? entry.vpcC;
        if (box) {
          const stream = new (MP4Box as any).DataStream(
            undefined,
            0,
            (MP4Box as any).DataStream.BIG_ENDIAN // IMPORTANT: must be BIG_ENDIAN
          );
          box.write(stream);
          return new Uint8Array(stream.buffer.slice(8)).buffer;
        }
      }
    } catch (error) {
      console.error('Failed to get video description:', error);
    }
    return undefined;
  }

  // private getVideoDescription(track: any): ArrayBuffer | undefined {
  //   if (!this.mp4boxFile) return undefined;
  //   return getVideoDescription(this.mp4boxFile, track);
  // }

  private getAudioDescription(track: any): ArrayBuffer | undefined {
    try {
      const fullTrack = this.mp4boxFile.getTrackById(track.id);
      for (const entry of fullTrack.mdia.minf.stbl.stsd.entries) {
        if (entry.esds || entry.dOps) {
          const stream = new (MP4Box as any).DataStream();
          (entry.esds || entry.dOps).write(stream);
          return new Uint8Array(stream.buffer.slice(8)).buffer;
        }
      }
    } catch (error) {
      console.error('Failed to get audio description:', error);
    }
    return undefined;
  }

  /**
   * Create transform stream for video track
   */
  createVideoStream(): TransformStream<Uint8Array, EncodedVideoChunk> {
    // const hasVideo = Array.from(this.tracks.values()).some((t) => t.type === 'video');
    return new TransformStream<Uint8Array, EncodedVideoChunk>(
      {
        start: (controller) => {
          this.videoController = controller;
        },
        transform: (chunk, _controller) => {
          // Create a copy to avoid buffer reuse issues (required for mp4box 0.5.x)
          const chunkData = new Uint8Array(chunk);
          this.appendBuffer(chunkData);

          // Flush after each append to trigger sample extraction immediately
          this.mp4boxFile.flush();
        },
        flush: async () => {
          // Trigger MP4Box flush
          this.mp4boxFile.flush();

          // Wait for MP4Box to complete sample extraction (onSamples callbacks)
          // Give MP4Box time to process asynchronously
          await new Promise((resolve) => setTimeout(resolve, 100));
        },
      },
      // Queuing strategy: use configuration
      {
        highWaterMark: this.demuxHighWaterMark,
        size: () => 1, // Count-based
      }
    );
  }

  /**
   * Create transform stream for audio track
   */
  createAudioStream(): TransformStream<Uint8Array, EncodedAudioChunk> | null {
    const hasAudio = Array.from(this.tracks.values()).some((t) => t.type === 'audio');
    if (!hasAudio) return null;

    return new TransformStream<Uint8Array, EncodedAudioChunk>(
      {
        start: (controller) => {
          this.audioController = controller;
        },
        transform: (chunk, _controller) => {
          // Create a copy to avoid buffer reuse issues (required for mp4box 0.5.x)
          const chunkData = new Uint8Array(chunk);
          this.appendBuffer(chunkData);

          // Flush after each append to trigger sample extraction immediately
          this.mp4boxFile.flush();
        },
        flush: () => {
          this.mp4boxFile.flush();
        },
      },
      // Queuing strategy: use configuration
      {
        highWaterMark: this.demuxHighWaterMark,
        size: () => 1,
      }
    );
  }

  appendBuffer(chunk: Uint8Array): void {
    const buffer = chunk.buffer as ArrayBuffer & { fileStart: number };
    buffer.fileStart = this.fileOffset;
    this.mp4boxFile.appendBuffer(buffer);
    this.fileOffset += chunk.byteLength;
  }

  /**
   * Get video track info if available
   */
  get videoTrackInfo(): TrackInfo | undefined {
    return Array.from(this.tracks.values()).find((track) => track.type === 'video');
  }

  /**
   * Get audio track info if available
   */
  get audioTrackInfo(): TrackInfo | undefined {
    return Array.from(this.tracks.values()).find((track) => track.type === 'audio');
  }

  destroy(): void {
    this.mp4boxFile?.stop();
    this.mp4boxFile = null;
    this.tracks.clear();
    this.isReady = false;
    this.videoTimestampOffset = null;
    this.audioTimestampOffset = null;
  }
}
